/**
 * Guía: Referidos entre clientes.
 *
 * Contenido derivado del capítulo **45** del manual: §0 (qué es el programa),
 * §3 (por qué aquí sí sale el consumo de una persona y en la app no) y §5
 * (qué significa cada columna y por qué cuadran entre sí).
 *
 * El punto que la guía existe para dejar claro es el que más cuesta explicar
 * por escrito: el consumo que se ve aquí **no es el total del pedido**. Es el
 * gasto elegible, la misma base con la que se calcularon los puntos, y por eso
 * las dos columnas se pueden dividir. Quien no lo sepa va a sumar totales de
 * otra pantalla, ver que no cuadra y abrir un ticket.
 */

import type { TourFlow } from '../types';

const RUTA = '/referral-report';

export const c45ReferidosEntreClientes: TourFlow = {
  id: 'admin.referralReport',
  chapter: '45',
  title: 'Referidos',
  summary:
    'Quién invitó a quién, qué ha generado cada relación, y por qué las cifras de aquí no se comparan con las de otras pantallas.',
  href: RUTA,
  requiresCountry: true,
  estimatedMin: 3,
  steps: [
    {
      id: 'intro',
      title: 'Una fila por invitación',
      body:
        'Cada fila es una relación entre dos clientes: alguien repartió su código y '
        + 'alguien llegó con él. Quien invita cobra un porcentaje de lo que su '
        + 'referido consuma, en Puntos 121, y quien llega recibe un bono de '
        + 'bienvenida. Esta pantalla es donde se comprueba que eso cuadra.',
      route: RUTA,
    },
    {
      id: 'kpis',
      title: 'Ojo con lo que suman las tarjetas',
      body:
        'Las relaciones son el total del informe, pero los puntos y el consumo son '
        + 'sólo los de esta página. No es un descuido: el informe se pagina en el '
        + 'servidor, y sumar aquí lo que no se trajo sería inventarlo. Si necesitas '
        + 'el total, acota con los filtros hasta que quepa.',
      target: 'admin.referralReport.kpis',
      placement: 'bottom',
    },
    {
      id: 'columnas',
      title: 'El consumo no es el total del pedido',
      body:
        'Es el gasto elegible: subtotal menos descuentos menos lo que el cliente pagó '
        + 'con puntos. Es la misma base con la que se calcularon los puntos que ves al '
        + 'lado, y por eso las dos columnas se pueden dividir para obtener el '
        + 'porcentaje vigente. Compararlo con el total de una factura no va a cuadrar, '
        + 'y no es un error del informe.',
      target: 'admin.referralReport.table',
      placement: 'top',
    },
    {
      id: 'estados',
      title: 'Registrado no es lo mismo que activo',
      body:
        'Quien se registró con un código pero todavía no ha comprado está REGISTRADO: '
        + 'no ocupa una de las veinte plazas de quien lo invitó y no le genera nada. '
        + 'Pasa a ACTIVO con su primera compra pagada. CANCELADO significa que quien '
        + 'invitaba cerró su cuenta; BLOQUEADO lo pone una revisión antifraude.',
    },
    {
      id: 'filtros',
      title: 'El filtro de consumo es el caro',
      body:
        'Todos los filtros se resuelven sobre la tabla salvo el de consumo, que suma '
        + 'los pedidos de cada referido antes de poder comparar. En una base grande '
        + 'conviene acotar también por fecha o por estado; si no, la consulta se '
        + 'arrastra.',
      target: 'admin.referralReport.filters',
      placement: 'bottom',
    },
    {
      id: 'privacidad',
      title: 'Este dato no sale de aquí',
      body:
        'El consumo de una persona identificada se muestra en este panel porque hay '
        + 'una finalidad que lo justifica —cuadrar lo emitido y detectar fraude— y un '
        + 'responsable detrás de cada consulta. En la app, quien invita ve sólo lo que '
        + 'él ganó, nunca lo que su amigo gastó. No traslades a un cliente cifras de '
        + 'esta pantalla que no sean suyas.',
    },
  ],
};
